import { useEffect } from "react";

import type { RefObject } from "react";

export const DWELL_DURATION_MS = 2000;
const DEFAULT_THRESHOLD = 0.5;

interface UseDwellObserverProps {
  elementRef: RefObject<Element>;
  onDwell: () => void;
  dwellMs?: number;
  threshold?: number;
  enabled?: boolean;
}

/*
  Fires `onDwell` once `elementRef` has stayed continuously on-screen (at least
  `threshold` of it visible) for `dwellMs`. The timer is cancelled if the element
  scrolls out or the tab is hidden before it elapses, so a quick scroll-past or a
  background tab does not count as a dwell.
*/
export function useDwellObserver(props: UseDwellObserverProps) {
  const {
    elementRef,
    onDwell,
    dwellMs = DWELL_DURATION_MS,
    threshold = DEFAULT_THRESHOLD,
    enabled = true,
  } = props;

  useEffect(() => {
    const element = elementRef.current;
    if (!element || !enabled) {
      return undefined;
    }

    let dwellTimer: ReturnType<typeof setTimeout> | undefined;
    let isVisible = false;

    const clearDwellTimer = () => {
      if (dwellTimer) {
        clearTimeout(dwellTimer);
        dwellTimer = undefined;
      }
    };

    const startDwellTimer = () => {
      if (dwellTimer || document.visibilityState !== "visible") {
        return;
      }
      dwellTimer = setTimeout(onDwell, dwellMs);
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        isVisible = entry.isIntersecting;
        if (isVisible) {
          startDwellTimer();
        } else {
          clearDwellTimer();
        }
      },
      { threshold },
    );

    // Pause when the tab is hidden and resume once it is visible again and the
    // element is still on screen (no fresh intersection event fires on refocus).
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        if (isVisible) {
          startDwellTimer();
        }
      } else {
        clearDwellTimer();
      }
    };

    observer.observe(element);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearDwellTimer();
      observer.disconnect();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [elementRef, onDwell, dwellMs, threshold, enabled]);
}
